import { NGROK } from "../../../../APIs";
import { apiClient } from "../../../../utils/apiClient";

export interface MultiApprovalAdmin {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  roleName: string;
  roleId: number;
}

export interface MultiApprovalAdminPage {
  content: MultiApprovalAdmin[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface MultiApprovalPage<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface MultiApprovalGroupMemberPayload {
  tenantAdminId: number;
  email: string;
  roleName: string;
  firstName: string;
  lastName: string;
}

export interface MultiApprovalGroupMember {
  tenantAdminId: number;
  email: string;
  roleName: string;
  firstName: string;
  lastName: string;
  fullName: string;
}

export interface MultiApprovalGroup {
  id: string;
  name: string;
  description: string;
  tenantName: string;
  members: MultiApprovalGroupMember[];
  createdAt?: string;
  updatedAt?: string;
  systemDefault?: boolean;
  hasActivePendingRequests?: boolean;
  editable?: boolean;
}

export interface CreateMultiApprovalGroupPayload {
  name: string;
  description: string;
  tenantName: string;
  members: MultiApprovalGroupMemberPayload[];
}

export interface UpdateMultiApprovalGroupPayload extends CreateMultiApprovalGroupPayload {
  uuid: string;
}

export const fetchMultiApprovalAdmins = async (
  tenantName: string,
  page = 0,
  size = 10,
  search?: string,
): Promise<MultiApprovalAdminPage> => {
  let url = `${NGROK}/api/multi-approval/${encodeURIComponent(tenantName)}/admins?page=${page}&size=${size}`;
  if (search?.trim()) {
    url += `&search=${encodeURIComponent(search.trim())}`;
  }
  return apiClient<MultiApprovalAdminPage>({ method: "GET", url });
};

export const fetchMultiApprovalGroups = async (
  tenantName: string,
  page = 0,
  size = 15,
  search?: string,
): Promise<MultiApprovalPage<MultiApprovalGroup>> => {
  let url = `${NGROK}/api/multi-approval/groups?tenantName=${encodeURIComponent(tenantName)}&page=${page}&size=${size}`;
  if (search?.trim()) {
    url += `&search=${encodeURIComponent(search.trim())}`;
  }
  return apiClient<MultiApprovalPage<MultiApprovalGroup>>({ method: "GET", url });
};

export const createMultiApprovalGroup = async (
  payload: CreateMultiApprovalGroupPayload,
): Promise<MultiApprovalGroup> => {
  const url = `${NGROK}/api/multi-approval/groups`;
  return apiClient<MultiApprovalGroup>({ method: "POST", url, data: payload });
};

export const updateMultiApprovalGroup = async (
  payload: UpdateMultiApprovalGroupPayload,
): Promise<MultiApprovalGroup> => {
  const url = `${NGROK}/api/multi-approval/groups`;
  return apiClient<MultiApprovalGroup>({ method: "PUT", url, data: payload });
};

export const deleteMultiApprovalGroup = async (uuid: string, tenantName: string): Promise<boolean> => {
  const url = `${NGROK}/api/multi-approval/groups/${encodeURIComponent(uuid)}?tenantName=${encodeURIComponent(tenantName)}`;
  return apiClient<boolean>({ method: "DELETE", url });
};

export interface MultiApprovalResourceItem {
  resourceKey: string;
  resourceSource: string;
  resourceSourceLabel: string;
  resourceType: string;
  resourceTypeLabel: string;
  resourceId: string;
  name: string;
  detail?: string;
}

export interface MultiApprovalResourceCategory {
  resourceSource: string;
  resourceSourceLabel: string;
  resourceType: string;
  resourceTypeLabel: string;
  resources: MultiApprovalResourceItem[];
}

export interface MultiApprovalResourcesResponse {
  categories: MultiApprovalResourceCategory[];
}

export interface MultiApprovalPolicyResource {
  resourceKey: string;
  resourceSource: string;
  resourceSourceLabel: string;
  resourceType: string;
  resourceTypeLabel: string;
  resourceId: string;
  resourceName: string;
}

export interface MultiApprovalPolicyRule {
  groupUuid: string;
  groupName?: string;
  minApprovals: number;
  ruleOrder?: number;
}

export interface MultiApprovalPolicy {
  id: string;
  name: string;
  description: string;
  status: "active" | "inactive";
  tenantName: string;
  resources: MultiApprovalPolicyResource[];
  rules: MultiApprovalPolicyRule[];
  createdAt?: string;
  updatedAt?: string;
  hasActivePendingRequests?: boolean;
  editable?: boolean;
}

export interface MultiApprovalPolicyResourcePayload {
  resourceSource: string;
  resourceType: string;
  resourceId: string;
  resourceName: string;
  resourceKey: string;
  resourceSourceLabel: string;
  resourceTypeLabel: string;
}

export interface MultiApprovalPolicyRulePayload {
  groupUuid: string;
  minApprovals: number;
  ruleOrder?: number;
}

export interface CreateMultiApprovalPolicyPayload {
  name: string;
  description: string;
  status: string;
  tenantName: string;
  resources: MultiApprovalPolicyResourcePayload[];
  rules: MultiApprovalPolicyRulePayload[];
}

export interface UpdateMultiApprovalPolicyPayload extends CreateMultiApprovalPolicyPayload {
  uuid: string;
}

export const fetchMultiApprovalResources = async (
  tenantName: string,
  limit = 5,
  search?: string,
  excludePolicyUuid?: string,
): Promise<MultiApprovalResourcesResponse> => {
  let url = `${NGROK}/api/multi-approval/${encodeURIComponent(tenantName)}/resources?limit=${limit}`;
  if (search?.trim()) {
    url += `&search=${encodeURIComponent(search.trim())}`;
  }
  if (excludePolicyUuid?.trim()) {
    url += `&excludePolicyUuid=${encodeURIComponent(excludePolicyUuid.trim())}`;
  }
  return apiClient<MultiApprovalResourcesResponse>({ method: "GET", url });
};

export const fetchMultiApprovalPolicies = async (
  tenantName: string,
  page = 0,
  size = 15,
  search?: string,
  status?: string,
): Promise<MultiApprovalPage<MultiApprovalPolicy>> => {
  let url = `${NGROK}/api/multi-approval/policies?tenantName=${encodeURIComponent(tenantName)}&page=${page}&size=${size}`;
  if (search?.trim()) {
    url += `&search=${encodeURIComponent(search.trim())}`;
  }
  if (status && status !== "all") {
    url += `&status=${encodeURIComponent(status)}`;
  }
  return apiClient<MultiApprovalPage<MultiApprovalPolicy>>({ method: "GET", url });
};

export const cancelActiveRequestsForPolicy = async (
  uuid: string,
  tenantName: string,
): Promise<number> => {
  const url = `${NGROK}/api/multi-approval/policies/${encodeURIComponent(uuid)}/cancel-active-requests?tenantName=${encodeURIComponent(tenantName)}`;
  return apiClient<number>({ method: "POST", url });
};

export const cancelActiveRequestsForGroup = async (
  uuid: string,
  tenantName: string,
): Promise<number> => {
  const url = `${NGROK}/api/multi-approval/groups/${encodeURIComponent(uuid)}/cancel-active-requests?tenantName=${encodeURIComponent(tenantName)}`;
  return apiClient<number>({ method: "POST", url });
};

export const createMultiApprovalPolicy = async (
  payload: CreateMultiApprovalPolicyPayload,
): Promise<MultiApprovalPolicy> => {
  const url = `${NGROK}/api/multi-approval/policies`;
  return apiClient<MultiApprovalPolicy>({ method: "POST", url, data: payload });
};

export const updateMultiApprovalPolicy = async (
  payload: UpdateMultiApprovalPolicyPayload,
): Promise<MultiApprovalPolicy> => {
  const url = `${NGROK}/api/multi-approval/policies`;
  return apiClient<MultiApprovalPolicy>({ method: "PUT", url, data: payload });
};

export const deleteMultiApprovalPolicy = async (uuid: string, tenantName: string): Promise<boolean> => {
  const url = `${NGROK}/api/multi-approval/policies/${encodeURIComponent(uuid)}?tenantName=${encodeURIComponent(tenantName)}`;
  return apiClient<boolean>({ method: "DELETE", url });
};

export type MultiApprovalRequestSource = "ENDPOINT" | "AWS" | "AZURE" | "GCP";

export type MultiApprovalGroupProgressStatus = "PENDING" | "COMPLETED";

export interface MultiApprovalRuleProgress {
  groupUuid: string;
  groupName: string;
  minApprovals: number;
  requiredApprovals: number;
  ruleOrder?: number;
  currentApprovals: number;
  remainingApprovals: number;
  status: MultiApprovalGroupProgressStatus;
  currentUserApproved: boolean;
  canCurrentUserApprove: boolean;
  currentUserIsMember: boolean;
  userMessage: string;
}

export interface MultiApprovalRequestAction {
  groupUuid: string;
  groupName: string;
  tenantAdminId?: number;
  approverEmail: string;
  approverFirstName?: string;
  approverLastName?: string;
  approverDisplayName?: string;
  approvedAt?: string;
}

export interface MultiApprovalGroupApprovalHistory {
  groupUuid: string;
  groupName: string;
  approvals: MultiApprovalRequestAction[];
}

export interface MultiApprovalWorkflow {
  workflowUuid: string;
  tenantName: string;
  requestSource: MultiApprovalRequestSource;
  requestId: number;
  policyUuid?: string;
  policyName?: string;
  resourceKey?: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string;
  ruleProgress: MultiApprovalRuleProgress[];
  actions: MultiApprovalRequestAction[];
  groupedApprovals: MultiApprovalGroupApprovalHistory[];
  underMultiApproval: boolean;
  allGroupsCompleted: boolean;
  currentUserCanApprove: boolean;
}

export const fetchMultiApprovalWorkflow = async (
  requestSource: MultiApprovalRequestSource,
  requestId: number,
  currentUserEmail?: string,
): Promise<MultiApprovalWorkflow | null> => {
  let url = `${NGROK}/api/multi-approval/workflows/${requestSource}/${requestId}`;
  if (currentUserEmail?.trim()) {
    url += `?currentUserEmail=${encodeURIComponent(currentUserEmail.trim())}`;
  }
  try {
    return await apiClient<MultiApprovalWorkflow>({ method: "GET", url });
  } catch {
    return null;
  }
};

export const fetchMultiApprovalWorkflowsBatch = async (
  requestSource: MultiApprovalRequestSource,
  requestIds: number[],
  currentUserEmail?: string,
): Promise<Record<number, MultiApprovalWorkflow>> => {
  if (requestIds.length === 0) {
    return {};
  }
  const url = `${NGROK}/api/multi-approval/workflows/${requestSource}/batch`;
  const raw = await apiClient<Record<string, MultiApprovalWorkflow>>({
    method: "POST",
    url,
    data: {
      requestIds,
      currentUserEmail: currentUserEmail?.trim() || undefined,
    },
  });
  const mapped: Record<number, MultiApprovalWorkflow> = {};
  Object.entries(raw ?? {}).forEach(([key, workflow]) => {
    mapped[Number(key)] = workflow;
  });
  return mapped;
};

/** True only when an active multi-approval policy workflow exists for this request. */
export function isMultiApprovalRequest(
  workflow: MultiApprovalWorkflow | null | undefined,
): workflow is MultiApprovalWorkflow {
  return workflow?.underMultiApproval === true;
}

export function hasCurrentUserApproved(
  workflow: MultiApprovalWorkflow | null | undefined,
): boolean {
  return (
    isMultiApprovalRequest(workflow)
    && workflow.ruleProgress.some((rule) => rule.currentUserApproved)
  );
}

/** Decline is allowed only for approvers who can still act on the workflow. */
export function canUserDeclineRequest(
  workflow: MultiApprovalWorkflow | null | undefined,
): boolean {
  if (!isMultiApprovalRequest(workflow)) {
    return true;
  }
  if (hasCurrentUserApproved(workflow)) {
    return false;
  }
  return workflow.currentUserCanApprove;
}
